import { nanosToMillis, TIME_FORMAT_NUMERICAL_SHORT } from '@/datetime/dateTime.utilities';
import { sqTimezones } from '@/utilities/datetime.constants';
import type { Timezone } from '@/datetime/timezone.service';
import moment from 'moment-timezone';

import clsx from 'clsx';
import { displayRange } from '@/trendData/duration.actions';
import { setAutoFocusTrendTab } from '@/main/layout.actions';

export interface SummaryReportWindowOfInterestProps {
  timezone: Timezone;
  intervalStart?: number;
  intervalEnd?: number;
  isPresentation?: boolean;
}

/**
 * Displays the window of interest (time range) for a statistics summary item.
 * Shows: start datetime - end datetime timezone
 * Read-only component that formats dates similar to DateTimeEntry.
 */
export const SummaryReportWindowOfInterest: React.FunctionComponent<SummaryReportWindowOfInterestProps> = ({
  timezone,
  intervalStart,
  intervalEnd,
  isPresentation = false,
}) => {
  if (!intervalStart || !intervalEnd) {
    return null;
  }
  const startMillis = nanosToMillis(intervalStart);
  const endMillis = nanosToMillis(intervalEnd);
  const startMoment = moment(startMillis).tz(timezone.name);
  const endMoment = moment(endMillis).tz(timezone.name);
  const formattedStartDate = startMoment.format(TIME_FORMAT_NUMERICAL_SHORT);
  const formattedEndDate = endMoment.format(TIME_FORMAT_NUMERICAL_SHORT);
  const timezoneAbbreviation = sqTimezones.getAbbreviatedDisplay(timezone, startMoment);

  const handleDateRangeClick = () => {
    if (isPresentation) {
      return;
    }
    displayRange.updateTimes(startMillis, endMillis);
    setAutoFocusTrendTab(true);
  };

  return (
    <div
      className={clsx('dateTimeEntry ml20 tw:font-semibold', {
        'force-link-look cursorPointer': !isPresentation,
      })}
      onClick={handleDateRangeClick}>
      <span>
        {formattedStartDate} - {formattedEndDate} {timezoneAbbreviation}
      </span>
    </div>
  );
};
